/**
 * SupplierOutreachDialog — populated mailto for vendor RFQ / availability requests.
 */
import React, { useMemo, useState } from 'react';
import { Copy, Mail, X } from 'lucide-react';
import { Button } from '../../ui/button';
import type { BomItem, Supplier } from '../../../types/supplyChain';
import {
  buildSupplierOutreachEmail,
  type SupplierOutreachContext,
} from '../../../services/supplyChain/buildSupplierOutreachEmail';
import { SCM_DATA_DISCLAIMER } from '../../../lib/scmDisclaimer';
import { partnershipBadgeLabel } from '../../../lib/supplierPartnership';
import { SupplierPartnershipBadge } from './SupplierPartnershipBadge';

export interface SupplierOutreachDialogProps {
  supplier: Supplier;
  bomItem: BomItem;
  project: SupplierOutreachContext;
  onClose: () => void;
}

export const SupplierOutreachDialog: React.FC<SupplierOutreachDialogProps> = ({
  supplier,
  bomItem,
  project,
  onClose,
}) => {
  const built = useMemo(
    () => buildSupplierOutreachEmail({ supplier, bomItem, project }),
    [supplier, bomItem, project],
  );
  const [copied, setCopied] = useState(false);
  const partnerLabel = partnershipBadgeLabel(supplier);

  const handleCopy = async () => {
    const full = built.toEmail
      ? `To: ${built.toEmail}\nSubject: ${built.subject}\n\n${built.body}`
      : `Subject: ${built.subject}\n\n${built.body}`;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* silent */ }
  };

  const handleMailto = () => {
    window.open(built.mailtoUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="supplier-outreach-title"
      data-testid="supplier-outreach-dialog"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 id="supplier-outreach-title" className="text-[11px] font-bold uppercase tracking-widest">
            Email {supplier.name}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-text-muted hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3 text-[10px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{supplier.name}</span>
            <SupplierPartnershipBadge supplier={supplier} />
          </div>

          <div className="rounded-md border border-warning/40 bg-warning/10 px-2 py-1.5 text-warning type-body">
            {SCM_DATA_DISCLAIMER}
          </div>

          {!built.toEmail && (
            <p className="type-body text-text-secondary">
              No contact email on file — add contacts in Admin → Supplier directory, or paste the draft below.
            </p>
          )}

          <div className="space-y-1">
            <div className="type-body font-bold uppercase tracking-widest text-text-secondary">Subject</div>
            <div className="border border-border px-2 py-1.5 bg-muted/30">{built.subject}</div>
          </div>

          <div className="space-y-1">
            <div className="type-body font-bold uppercase tracking-widest text-text-secondary">Body</div>
            <pre className="border border-border px-2 py-2 bg-muted/30 whitespace-pre-wrap font-sans text-[10px] max-h-64 overflow-auto">
              {built.body}
            </pre>
          </div>

          {partnerLabel && (
            <p className="type-body text-success">
              This supplier has a {partnerLabel.toLowerCase()} with Datacrate — reference existing agreement in your message if applicable.
            </p>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button type="button" size="sm" variant="outline" onClick={() => { void handleCopy(); }} data-testid="supplier-outreach-copy">
              <Copy className="w-3 h-3 mr-1" />
              {copied ? 'Copied' : 'Copy draft'}
            </Button>
            <Button type="button" size="sm" onClick={handleMailto} data-testid="supplier-outreach-mailto">
              <Mail className="w-3 h-3 mr-1" />
              Open in mail client
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
